import React, { useMemo } from "react";
import { getServerSideSplitTreatments } from "@leafly-com/split-next";
import { GetServerSidePropsContext } from "next";
import { useSelector } from "react-redux";

import { FILTER_CONFIG_OPTIONS } from "api/requests/consumerApi/getMenuItemsV3";
import { getBrandV3Show } from "api/requests/getBrandV3Show";
import { getIsFollowing } from "api/requests/getIsFollowing";
import { Category } from "constants/events";
import { BrandDetail } from "custom-types/BrandDetail";
import useDispensaries from "hooks/shop/useDispensaries";
import useQueryParamState from "hooks/useQueryParamState";
import { getCoordinates } from "redux/selectors/location";
import { getCountryCodeHeader } from "utils/requestContext";
import { getSplitKey } from "utils/split/getSplitKey";

import BrandLayoutWrapper from "components/brands/BrandLayoutWrapper";
import DeliveryMenu from "components/DeliveryGateway/DeliveryMenu";
import HeaderV2 from "components/DeliveryGateway/HeaderV2";
import FulfillmentMode from "components/DeliveryGateway/types/FulfillmentMode";
import { MenuPage } from "components/MenuFilters/types/MenuFilters";

const Shop = ({
  brand,
  isFollowing,
  showShopExperience,
}: {
  brand: BrandDetail;
  isFollowing: boolean;
  showShopExperience: boolean;
}) => {
  //prevents re-renders and extra api calls
  const permanentFilters = useMemo(
    () => ({ brand_name: [brand.name] }),
    [brand.name],
  );
  const coordinates = useSelector(getCoordinates);
  const { deliveryDispensaries, loading: loadingDispensaries } =
    useDispensaries();
  const [fulfillmentPreference] = useQueryParamState("fulfillmentPreference");
  let fulfillmentMode: FulfillmentMode;

  if (fulfillmentPreference) {
    fulfillmentMode = fulfillmentPreference as FulfillmentMode;
  } else if (deliveryDispensaries.length > 0) {
    fulfillmentMode = "delivery";
  } else {
    fulfillmentMode = "pickup";
  }

  return (
    <BrandLayoutWrapper
      brand={brand}
      referringPage="brand-shop"
      wrapperClassName="container"
      showShopExperience={showShopExperience}
    >
      <HeaderV2
        fulfillmentMode={fulfillmentMode}
        hideDivider
        wrapperClassName="mt-none"
        eventCategory={Category.brandPaid}
      />
      <DeliveryMenu
        brandId={brand.id}
        brandName={brand.name}
        coordinates={coordinates}
        filterConfig={FILTER_CONFIG_OPTIONS.BRAND_MENU}
        fulfillmentLoading={loadingDispensaries}
        isFollowing={isFollowing}
        isDeliveryFulfillmentMode={fulfillmentMode === "delivery"}
        isPickupFulfillmentMode={fulfillmentMode === "pickup"}
        permanentFilters={permanentFilters}
        showEmptyResultsMessaging={false}
        eventCategory={Category.brandPaid}
        menuPage={MenuPage.brand}
        hideCoinList
      />
    </BrandLayoutWrapper>
  );
};

export const getServerSideProps = async (
  context: GetServerSidePropsContext,
) => {
  const { slug, categorySlug } = context.query;

  if (categorySlug && categorySlug.length > 1) {
    return {
      notFound: true,
    };
  }
  const brand = await getBrandV3Show(String(slug));
  const countryCode = getCountryCodeHeader(context.req.headers);

  let isFollowing = false;
  if (brand !== undefined) {
    isFollowing = await getIsFollowing({
      countryCode,
      followeeId: brand.id,
      followeeType: "brand",
    });
  }

  const {
    "NL-1116_brandsShopTab_flag": { treatment },
  } = await getServerSideSplitTreatments(
    "NL-1116_brandsShopTab_flag",
    getSplitKey(context),
  );

  return {
    props: {
      brand,
      isFollowing,
      showShopExperience: treatment === "on",
    },
  };
};

export default Shop;
